import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Select from "../../../shared/ui/Field/Select";
import Textarea from "../../../shared/ui/Field/Textarea";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import {
  messageFromError,
  parseDrfFieldErrors,
} from "../../../shared/api/drfErrors";

import {
  fetchCompany,
  fetchMembers,
  fetchMyCompany,
  leaveCompany,
  patchMember,
  removeMember,
  resumeMember,
  setFinancePermission,
  suspendMember,
  transferOwnership,
} from "../api/companyWorkspace";
import {
  canEditMemberTarget,
  canGrantFinance,
  canOfferSelfLeave,
  canRemoveMemberTarget,
  canResumeMemberTarget,
  canRevokeFinance,
  canSuspendMemberTarget,
  canTransferToMember,
  isActiveMembership,
  isAdminMembership,
  memberDisplayName,
  membershipRole,
  membershipStatusLabel,
  ownerLeaveRequiresTransfer,
  roleLabel,
} from "../lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../lib/companyFocusRevalidation";
import CompanyPageHeader from "../ui/CompanyPageHeader";

const STATUS_TABS = [
  { id: "active", label: "Активні" },
  { id: "suspended", label: "Призупинені" },
  { id: "ended", label: "Завершені" },
  { id: "all", label: "Усі" },
];

function MemberEditForm({
  member,
  actorRole,
  busy,
  fieldErrors = {},
  onFieldChange,
  onSave,
  onCancel,
}) {
  const canChangeRole =
    actorRole === "owner" &&
    member.role !== "owner" &&
    member.status === "active";
  const [role, setRole] = useState(member.role);
  const [position, setPosition] = useState(member.position || "");
  const [bio, setBio] = useState(member.bio || "");

  const clearAndSet = (name, setter) => (event) => {
    const next = event.target.value;
    setter(next);
    if (onFieldChange) onFieldChange(name);
  };

  return (
    <form
      className="feature-company-workspace__member-edit"
      onSubmit={(event) => {
        event.preventDefault();
        const body = { position, bio };
        if (canChangeRole && role !== "owner") body.role = role;
        onSave(body);
      }}
    >
      {canChangeRole ? (
        <Field id={`member-role-${member.id}`} label="Роль" error={fieldErrors.role}>
          <Select
            name="role"
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              if (onFieldChange) onFieldChange("role");
            }}
            options={[
              { value: "admin", label: "Керівник" },
              { value: "member", label: "Рієлтор" },
            ]}
          />
        </Field>
      ) : null}
      <Field
        id={`member-position-${member.id}`}
        label="Посада"
        error={fieldErrors.position}
      >
        <TextInput
          name="position"
          value={position}
          onChange={clearAndSet("position", setPosition)}
        />
      </Field>
      <Field id={`member-bio-${member.id}`} label="Біо" error={fieldErrors.bio}>
        <Textarea
          name="bio"
          value={bio}
          onChange={clearAndSet("bio", setBio)}
          rows={3}
        />
      </Field>
      <div className="feature-company-workspace__inline-actions">
        <button type="submit" className="action-btn" disabled={busy}>
          Зберегти
        </button>
        <button
          type="button"
          className="action-btn action-btn--ghost"
          onClick={onCancel}
          disabled={busy}
        >
          Скасувати
        </button>
      </div>
    </form>
  );
}

function CompanyMembersContent() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [members, setMembers] = useState([]);
  const [tab, setTab] = useState("active");
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  const [writesSuspended, setWritesSuspended] = useState(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [memberFieldErrors, setMemberFieldErrors] = useState({});
  const [busyId, setBusyId] = useState(null);

  const companyMatchesRoute =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatchesRoute && actorMatches;
  const actorRole = authorityMatches
    ? membershipRole(company, membership)
    : null;
  const canManageRoster =
    authorityMatches && isAdminMembership(company, membership);

  const releaseBusy = (op) => {
    if (busyOpRef.current === op) {
      busyOpRef.current = null;
      setBusyId(null);
    }
  };

  const load = useCallback(async ({ background = false } = {}) => {
    if (!companyId) {
      setState("not-found");
      return;
    }
    const keepVisible = background && stateRef.current === "ready";
    const op = scopeGate.beginRead({ companyId, actorId });
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
      setError(null);
    }
    try {
      const [companyData, mine] = await Promise.all([
        fetchCompany(companyId),
        fetchMyCompany(),
      ]);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const mineMatches =
        mine?.company?.id != null && sameCompanyId(mine.company.id, companyId)
          ? mine.membership
          : null;
      setCompany(companyData);
      setMembership(mineMatches);
      setAuthorityActorId(actorId);
      if (!isActiveMembership(companyData, mineMatches)) {
        setMembers([]);
        setEditingId(null);
        setMemberFieldErrors({});
        setState("denied");
        return;
      }
      const manage = isAdminMembership(companyData, mineMatches);
      const statusScope = manage ? tab : "active";
      const roster = await fetchMembers(companyId, statusScope);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      setMembers(Array.isArray(roster) ? roster : []);
      revalidationConfirmed = true;
      setActionError((current) =>
        current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
      );
      setState("ready");
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (keepVisible && status !== 403 && status !== 404) {
        setActionError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
        return;
      }
      setMembers([]);
      setEditingId(null);
      setMemberFieldErrors({});
      setAuthorityActorId(null);
      if (status === 403) setState("denied");
      else if (status === 404) setState("not-found");
      else {
        setError(messageFromError(err));
        setState("error");
      }
    } finally {
      settleRevalidationWrites({
        keepVisible: keepVisible,
        confirmed: revalidationConfirmed,
        token: backgroundToken,
        tokenAtStart,
        tokenRef: revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      });
    }
  }, [actorId, companyId, scopeGate, tab]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ companyId, actorId });
    busyOpRef.current = null;
    setBusyId(null);
    setMembers([]);
    setEditingId(null);
    setMemberFieldErrors({});
    setActionError(null);
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
    };
  }, [companyId, actorId, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready",
    busy: busyId != null,
    onRevalidate: () => load({ background: true }),
  });

  const withAction = async (memberId, fn) => {
    if (busyOpRef.current || writesSuspendedRef.current) return;
    const op = scopeGate.beginMutation({
      companyId,
      actorId,
      resourceId: memberId,
    });
    busyOpRef.current = op;
    setBusyId(memberId);
    setActionError(null);
    try {
      // Explicit false = handled without canonical reload (e.g. field 400).
      const result = await fn();
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      if (result === false) {
        return;
      }
      await load();
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        setActionError(messageFromError(err));
        await load();
      } else if (status === 409) {
        setActionError(messageFromError(err));
        await load();
      } else {
        setActionError(messageFromError(err));
      }
    } finally {
      releaseBusy(op);
    }
  };

  const onLeave = async () => {
    if (!company || busyOpRef.current || writesSuspendedRef.current) return;
    if (!canOfferSelfLeave(company, membership)) return;
    if (ownerLeaveRequiresTransfer(company, membership)) {
      window.alert(
        `Власник активної компанії не може просто вийти. Спочатку передайте власність активному учаснику компанії «${company.name}» (кнопка «Передати власність» біля учасника).`,
      );
      return;
    }
    const confirmed = window.confirm(
      `Покинути компанію «${company.name}»? Корпоративний доступ буде втрачено. Особисті дані залишаться вашими.`,
    );
    if (!confirmed) return;
    const op = scopeGate.beginMutation({ companyId, actorId });
    busyOpRef.current = op;
    setBusyId("self-leave");
    try {
      await leaveCompany(companyId);
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      navigate("/companies/mine", { replace: true });
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      setActionError(messageFromError(err));
    } finally {
      releaseBusy(op);
    }
  };

  const onTransfer = async (member) => {
    if (!canTransferToMember(company, membership, member, membership?.id)) {
      return;
    }
    const name = memberDisplayName(member);
    const confirmed = window.confirm(
      `Передати власність компанії «${company.name}» користувачу ${name}? Дія незворотна.`,
    );
    if (!confirmed) return;
    await withAction(member.id, async () => {
      await transferOwnership(companyId, member.user.id);
    });
  };

  if (state === "loading" || (state === "ready" && !authorityMatches)) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження команди" />
      </div>
    );
  }

  if (state === "denied" || state === "not-found" || state === "error") {
    return (
      <div className="feature-company-workspace__state">
        <EmptyState
          icon="fa-solid fa-lock"
          title={
            state === "not-found"
              ? "Компанію не знайдено"
              : state === "error"
                ? "Помилка"
                : "Немає доступу"
          }
          description={
            error ||
            "Список учасників доступний лише активним членам компанії."
          }
          size="md"
        />
        {state === "error" ? (
          <button type="button" className="action-btn" onClick={load}>
            Повторити
          </button>
        ) : (
          <Link to="/companies/mine" className="action-btn">
            Моя компанія
          </Link>
        )}
      </div>
    );
  }

  if (!authorityMatches) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження команди" />
      </div>
    );
  }

  return (
    <section className="clients-page feature-company-workspace" id="Csection">
      <div className="container">
        <CompanyPageHeader
          title="Команда"
          subtitle="Керування ролями, призупиненням і виходом. Після передачі власності оновіть сторінку дій."
          company={company}
          membership={membership}
          companyId={companyId}
          activeNav="members"
          actions={
            canOfferSelfLeave(company, membership) ? (
              <button
                type="button"
                className="action-btn action-btn--danger"
                onClick={onLeave}
                disabled={busyId === "self-leave" || writesSuspended}
              >
                Покинути компанію
              </button>
            ) : null
          }
        />

        {ownerLeaveRequiresTransfer(company, membership) ? (
          <div className="feature-company-workspace__banner" role="status">
            Щоб вийти як власник активної компанії, спочатку передайте власність
            активному учаснику кнопкою «Передати власність».
          </div>
        ) : null}

        {canManageRoster ? (
          <div className="feature-company-workspace__tabs" role="tablist">
            {STATUS_TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                className={[
                  "feature-company-workspace__tab",
                  tab === item.id ? "feature-company-workspace__tab--active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setTab(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        ) : null}

        {actionError ? (
          <p className="feature-company-workspace__error" role="alert">
            {actionError}
          </p>
        ) : null}

        {members.length === 0 ? (
          <EmptyState
            icon="fa-solid fa-users"
            title="Немає учасників у цьому статусі"
            description="Змініть вкладку або запросіть нових учасників."
            size="md"
          />
        ) : (
          <ul className="feature-company-workspace__list">
            {members.map((member) => {
              const canEdit = canEditMemberTarget(company, membership, member);
              const canSuspend = canSuspendMemberTarget(
                company,
                membership,
                member,
              );
              const canResume = canResumeMemberTarget(
                company,
                membership,
                member,
              );
              const canRemove = canRemoveMemberTarget(
                company,
                membership,
                member,
              );
              const canTransfer = canTransferToMember(
                company,
                membership,
                member,
                membership?.id,
              );
              const canFinanceOn =
                canGrantFinance(company, membership, member) &&
                !member.can_access_company_finance;
              const canFinanceOff = canRevokeFinance(
                company,
                membership,
                member,
              );

              return (
                <li key={member.id} className="feature-company-workspace__card">
                  <div className="feature-company-workspace__card-top">
                    <div>
                      <h2>{memberDisplayName(member)}</h2>
                      <p>
                        {roleLabel(member.role)} ·{" "}
                        {membershipStatusLabel(member.status)}
                        {member.position ? ` · ${member.position}` : ""}
                      </p>
                    </div>
                    <div className="feature-company-workspace__inline-actions">
                      {canEdit ? (
                        <button
                          type="button"
                          className="action-btn action-btn--ghost"
                          disabled={writesSuspended}
                          onClick={() => {
                            if (writesSuspended) return;
                            setMemberFieldErrors({});
                            setEditingId(
                              editingId === member.id ? null : member.id,
                            );
                          }}
                        >
                          Редагувати
                        </button>
                      ) : null}
                      {canSuspend ? (
                        <button
                          type="button"
                          className="action-btn action-btn--ghost"
                          disabled={busyId === member.id || writesSuspended}
                          onClick={() =>
                            withAction(member.id, async () => {
                              const ok = window.confirm(
                                `Призупинити ${memberDisplayName(member)} у «${company.name}»?`,
                              );
                              if (!ok) return;
                              await suspendMember(companyId, member.id);
                            })
                          }
                        >
                          Призупинити
                        </button>
                      ) : null}
                      {canResume ? (
                        <button
                          type="button"
                          className="action-btn"
                          disabled={busyId === member.id || writesSuspended}
                          onClick={() =>
                            withAction(member.id, () =>
                              resumeMember(companyId, member.id),
                            )
                          }
                        >
                          Відновити
                        </button>
                      ) : null}
                      {canRemove ? (
                        <button
                          type="button"
                          className="action-btn action-btn--danger"
                          disabled={busyId === member.id || writesSuspended}
                          onClick={() =>
                            withAction(member.id, async () => {
                              const ok = window.confirm(
                                `Завершити членство ${memberDisplayName(member)} у «${company.name}»? Повторне відновлення цього запису недоступне — потрібен новий вступ.`,
                              );
                              if (!ok) return;
                              await removeMember(companyId, member.id);
                            })
                          }
                        >
                          Завершити
                        </button>
                      ) : null}
                      {canTransfer ? (
                        <button
                          type="button"
                          className="action-btn"
                          disabled={busyId === member.id || writesSuspended}
                          onClick={() => onTransfer(member)}
                        >
                          Передати власність
                        </button>
                      ) : null}
                      {canFinanceOn || canFinanceOff ? (
                        <button
                          type="button"
                          className="action-btn action-btn--ghost"
                          disabled={busyId === member.id || writesSuspended}
                          onClick={() =>
                            withAction(member.id, () =>
                              setFinancePermission(
                                companyId,
                                member.id,
                                Boolean(canFinanceOn),
                              ),
                            )
                          }
                        >
                          {canFinanceOff
                            ? "Відкликати дозвіл фінансів"
                            : "Надати дозвіл фінансів"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {member.can_access_company_finance && member.role === "admin" ? (
                    <p className="feature-company-workspace__note">
                      Дозвіл на корпоративні фінанси увімкнено (налаштування
                      доступу, не бухгалтерський облік).
                    </p>
                  ) : null}
                  {editingId === member.id ? (
                    <MemberEditForm
                      member={member}
                      actorRole={actorRole}
                      busy={busyId === member.id || writesSuspended}
                      fieldErrors={memberFieldErrors}
                      onFieldChange={(name) => {
                        setMemberFieldErrors((prev) => {
                          if (!prev?.[name]) return prev;
                          const next = { ...prev };
                          delete next[name];
                          return next;
                        });
                      }}
                      onCancel={() => {
                        setEditingId(null);
                        setMemberFieldErrors({});
                      }}
                      onSave={(body) =>
                        withAction(member.id, async () => {
                          try {
                            await patchMember(companyId, member.id, body);
                            setEditingId(null);
                            setMemberFieldErrors({});
                          } catch (err) {
                            const status = err?.response?.status;
                            if (status === 400) {
                              const parsed = parseDrfFieldErrors(err, [
                                "position",
                                "bio",
                                "role",
                              ]);
                              const { _form, ...fields } = parsed;
                              if (Object.keys(fields).length > 0) {
                                setMemberFieldErrors(fields);
                                // Skip canonical reload so rejected input stays mounted.
                                return false;
                              }
                            }
                            throw err;
                          }
                        })
                      }
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        {actionError ? <InlineError>{actionError}</InlineError> : null}
      </div>
    </section>
  );
}

export default CompanyMembersContent;
